import React, { useMemo, useState, useEffect } from 'react'
import { Settings, Link as LinkIcon, UserPlus } from 'lucide-react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import {
  Button, SegmentedToggle, Skeleton, Dialog, DialogContent, DialogHeader, DialogTitle,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, MultiPick, Badge,
} from '@duck/ui'
import { toast } from 'sonner'
import { useTar } from '@/modules/i18n'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { useAuth } from '@/hooks/useAuth'
import { useCohort, useUpdateCohort } from '@/hooks/useCohorts'
import { usePersons } from '@/hooks/usePersons'
import { useAssignments } from '@/hooks/useAssignments'
import { useTaskDisplayNames } from '@/modules/entityLookups'
import { useOrganizationSettings, useOrganizationAdminSettings } from '@/hooks/useOrganizationSettings'
import { CreateCohortDialog } from '../CreateCohortDialog'
import { DeleteDialog } from '@/components/DeleteDialog/DeleteDialog'
import { AddCohortPersonDialog } from '../People/AddCohortPersonDialog'
import { SendAssignment } from '../Assignments/SendAssignment'
import { AssignmentsTab } from './assignments/AssignmentsTab'
import { PeopleTab } from './people/PeopleTab'
import { ErrorState } from '@/components/EmptyStates'

interface CohortPageV2Props {
  cohortId: string
}

type Tab = 'assignments' | 'people'

export const CohortPageV2: React.FC<CohortPageV2Props> = ({ cohortId }) => {
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const { organizationId } = useAuth()
  const { cohort, assignmentsDetails, publicAssignments, isLoading, isError, error, refetch } = useCohort(cohortId)
  // Resolve names for BOTH cohort members and public-link joiners (joiners are NOT in
  // cohort.personIds). usePersons fetches globally and filters, so the union covers both.
  const personIdsToFetch = useMemo(() => {
    const ids = new Set<string>(cohort?.personIds ?? [])
    for (const d of assignmentsDetails ?? []) ids.add(d.personId)
    return Array.from(ids)
  }, [cohort?.personIds, assignmentsDetails])
  const { persons, isLoading: personsLoading } = usePersons(personIdsToFetch)
  const { getOrgSetting } = useOrganizationSettings()
  const { getOrgAdminSetting } = useOrganizationAdminSettings()
  const updateCohort = useUpdateCohort(cohortId, organizationId ?? undefined)

  const assignmentIds = useMemo(
    () => Array.from(new Set((assignmentsDetails ?? []).map((d) => d.assignmentId))),
    [assignmentsDetails]
  )
  const { assignments: assignmentsMap, isLoading: assignmentsLoading } = useAssignments(assignmentIds)
  const assignments = useMemo(() => {
    const byId = new Map(Object.values(assignmentsMap ?? {}).map((a) => [a.id, a] as const))
    // Public links with no submissions yet have no assignmentsDetails, so they never made it
    // into assignmentsMap. Merge them from useCohort's publicAssignments so empty public
    // assignments still appear (and are counted) rather than vanishing until first submission.
    for (const a of publicAssignments ?? []) if (!byId.has(a.id)) byId.set(a.id, a)
    return Array.from(byId.values())
  }, [assignmentsMap, publicAssignments])

  // Challenge selector (Assignments tab): multi-select; an empty selection means "all challenges".
  // Drop any selected ids that no longer exist after a refetch.
  const availableTaskIds = useMemo(
    () => Array.from(new Set(assignments.map((a) => a.taskId))),
    [assignments]
  )
  const { getDisplayName: getTaskDisplayName, isLoading: taskNamesLoading } = useTaskDisplayNames(availableTaskIds)
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([])
  useEffect(() => {
    setSelectedTaskIds((prev) => prev.filter((id) => availableTaskIds.includes(id)))
  }, [availableTaskIds])

  // The People tab shows ONLY manually-added cohort members (not public-link joiners), per the
  // product definition of "candidates". The Assignments tab keeps the full `persons` union so it
  // can resolve joiner names in public rosters.
  const cohortMembers = useMemo(() => {
    const ids = new Set(cohort?.personIds ?? [])
    return persons.filter((p) => ids.has(p.id))
  }, [persons, cohort?.personIds])

  // Header summary counts (each shown only when > 0).
  const publicCount = useMemo(() => assignments.filter((a) => a.linkToken).length, [assignments])
  const sentCount = assignments.length - publicCount
  const peopleCount = cohort?.personIds?.length ?? 0

  const allowPublicLinks = getOrgAdminSetting('allowPublicLinks') ?? false

  const [tab, setTab] = useState<Tab>('assignments')
  const [checkedPersonIds, setCheckedPersonIds] = useState<Set<string>>(new Set())
  const [addOpen, setAddOpen] = useState(false)
  const [sendOpen, setSendOpen] = useState(false)
  const [publicOpen, setPublicOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [closeOpen, setCloseOpen] = useState(false)

  const loading = isLoading || personsLoading

  const handleTabChange = (next: Tab) => {
    if (next === 'assignments') setCheckedPersonIds(new Set())
    setTab(next)
  }

  const goSend = () => {
    if (checkedPersonIds.size === 0) {
      setTab('people')
      toast.error(tP('pleaseSelectPeople'))
      return
    }
    setSendOpen(true)
  }

  if (isError || (!isLoading && !cohort)) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-foreground">{tP('cohort')}</h1>
        {error ? (
          <ErrorState
            title={t('common.errorLoading', { item: tP('cohort').toLowerCase() })}
            description={t('common.errorLoadingDescription')}
            onRetry={() => refetch()}
          />
        ) : (
          <ErrorState
            icon={MagnifyingGlassIcon}
            title={t('common.notFound', { item: tP('cohort') })}
            description={t('common.notFoundDescription')}
          />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {loading || !cohort ? (
            <><Skeleton className="h-9 w-1/3" /><Skeleton className="mt-2 h-4 w-1/2" /></>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-foreground">
                {cohort.name}
                {!cohort.isActive && <span className="text-muted-foreground"> ({t('partner.closed')})</span>}
              </h1>
              {(() => {
                const parts: string[] = []
                if (sentCount > 0) parts.push(t('partner.assignmentsCount', { count: sentCount }))
                if (publicCount > 0) parts.push(t('partner.publicAssignmentsCount', { count: publicCount }))
                if (peopleCount > 0) parts.push(tP('assignedPeopleCount', { count: peopleCount }))
                return parts.length > 0 ? (
                  <p className="mt-1 text-sm text-muted-foreground">{parts.join(' · ')}</p>
                ) : null
              })()}
              {cohort.description && <p className="mt-2 text-sm text-muted-foreground">{cohort.description}</p>}
            </>
          )}
        </div>
        {!loading && cohort && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('partner.cohortSettings')}><Settings className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditOpen(true)}>{tP('editDetails')}</DropdownMenuItem>
              {cohort.isActive ? (
                <DropdownMenuItem onClick={() => setCloseOpen(true)}>{tP('closeCohort')}</DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={async () => {
                  try {
                    await updateCohort.mutateAsync({
                      name: cohort.name, description: cohort.description, organizationId: cohort.organizationId,
                      type: cohort.type, isActive: true, personIds: cohort.personIds,
                    })
                    toast.success(tP('cohortReopened'))
                  } catch { toast.error(t('errors.somethingWentWrongWeveBeenNotified')) }
                }}>{tP('reopenCohort')}</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Tabs + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedToggle<Tab>
          value={tab}
          onChange={handleTabChange}
          segments={[
            { value: 'assignments', label: t('partner.assignments') },
            { value: 'people', label: tP('people') },
          ]}
        />
        <div className="flex items-center gap-2">
          {tab === 'assignments' ? (
            allowPublicLinks && (
              <Button className="gap-2" disabled={!cohort?.isActive} title={!cohort?.isActive ? tP('cohortClosedActionsDisabled') : undefined} onClick={() => setPublicOpen(true)}>
                <LinkIcon className="h-4 w-4" />{t('partner.createPublicAssignment')}
              </Button>
            )
          ) : (
            <Button className="gap-2" disabled={!cohort?.isActive} title={!cohort?.isActive ? tP('cohortClosedActionsDisabled') : undefined} onClick={() => setAddOpen(true)}>
              <UserPlus className="h-4 w-4" />{tP('addPeople')}
            </Button>
          )}
        </div>
      </div>

      {/* Challenge selector (Assignments tab) — its own row. Empty selection = all challenges. */}
      {tab === 'assignments' && availableTaskIds.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">{t('challenges.challenge')}:</span>
          {availableTaskIds.length === 1 ? (
            <Badge variant="outline">{taskNamesLoading ? t('common.loading') : getTaskDisplayName(availableTaskIds[0])}</Badge>
          ) : (
            <MultiPick
              className="w-80"
              searchable={false}
              options={availableTaskIds.map((id) => ({ label: taskNamesLoading ? t('common.loading') : getTaskDisplayName(id), value: id }))}
              selected={selectedTaskIds}
              onChange={setSelectedTaskIds}
              labels={{ placeholder: t('common.all') }}
            />
          )}
        </div>
      )}

      {/* Body */}
      {tab === 'assignments' ? (
        <AssignmentsTab
          assignments={assignments}
          assignmentsDetails={assignmentsDetails ?? []}
          persons={persons}
          isLoading={loading || assignmentsLoading}
          selectedTaskIds={selectedTaskIds}
          onAssignmentChange={refetch}
        />
      ) : (
        <PeopleTab
          cohortId={cohortId}
          persons={cohortMembers}
          assignmentsDetails={assignmentsDetails ?? []}
          assignments={assignments}
          isLoading={loading}
          checkedPersonIds={checkedPersonIds}
          setCheckedPersonIds={setCheckedPersonIds}
          onSendToSelected={goSend}
        />
      )}

      {/* Dialogs */}
      <AddCohortPersonDialog
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        cohortId={cohortId}
        onPersonAdded={(createdPersonIds) => {
          setTab('people')
          setCheckedPersonIds(new Set(createdPersonIds)) // auto-select → selection bar nudges Send
          refetch()
        }}
        onAssignmentSent={() => {
          setTab('assignments')
          refetch()
        }}
      />

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent width="sm">
          <DialogHeader><DialogTitle>{t('partner.sendAssignment')}</DialogTitle></DialogHeader>
          <SendAssignment
            cohortId={cohortId}
            personIds={Array.from(checkedPersonIds)}
            publicMode={false}
            sendInvitationEmails={getOrgSetting('invitationEmails') ?? false}
            onClose={() => setSendOpen(false)}
            onSend={() => { setSendOpen(false); setCheckedPersonIds(new Set()); setTab('assignments'); refetch() }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={publicOpen} onOpenChange={setPublicOpen}>
        <DialogContent width="sm">
          <DialogHeader><DialogTitle>{t('partner.createPublicAssignment')}</DialogTitle></DialogHeader>
          <SendAssignment
            cohortId={cohortId}
            publicMode
            onClose={() => setPublicOpen(false)}
            onSend={() => { setPublicOpen(false); setTab('assignments'); refetch() }}
          />
        </DialogContent>
      </Dialog>

      <CreateCohortDialog isOpen={editOpen} onClose={() => setEditOpen(false)} cohort={cohort || null} onCohortUpdated={refetch} />

      <DeleteDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        title={tP('closeCohort')}
        description={tP('closeCohortDescription')}
        deleteButtonText={tP('closeCohort')}
        onDelete={async () => {
          if (!cohort) throw new Error('Cohort is missing')
          await updateCohort.mutateAsync({
            name: cohort.name, description: cohort.description, organizationId: cohort.organizationId,
            type: cohort.type, isActive: false, personIds: cohort.personIds,
          })
        }}
      />
    </div>
  )
}
